/* feature:slowmo — pacing of the fixed-tick simulation loop.
   The time scale only decides how many fixed ticks run per real second, never the tick length, so a battle at 0.1x or 4x
   is bit-identical to one at 1x. Ticks per loop are capped, and the scale is lowered automatically (cap) when the worker
   cannot keep up; the cap is reported to the client. In lockstep multiplayer the pacer is locked to 1x. */
export const SLOWMO_MIN = 0.1, SLOWMO_MAX = 4;

const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
const floor05 = v => Math.floor(v * 20 + 1e-9) / 20; // caps in 0.05 steps, rounded down

export function createPacer({ tick = 1 / 30, maxTicks = 8, budget = 0.85, window = 0.5, maxWaitMs = 50 } = {}) {
  let cap = SLOWMO_MAX, locked = false, acc = 0, scale = 1;
  let msPerTick = 0, winT = 0, announce = false;
  const P = {
    get cap() { return cap; },
    get locked() { return locked; },
    /** scale that really runs: the requested one, limited by the overload cap (1 when locked) */
    effective(s = scale) { return locked ? 1 : clamp(Math.min(+s || 1, cap), SLOWMO_MIN, SLOWMO_MAX); },
    setLocked(on) { locked = !!on; acc = Math.min(acc, tick); },
    /** new battle: forget the load estimate (the unit count changed) */
    reset() { announce = cap !== SLOWMO_MAX; cap = SLOWMO_MAX; msPerTick = 0; winT = 0; acc = 0; },
    /** real seconds since the last call -> number of fixed ticks to run now (0 while stopped) */
    advance(realDt, running, s) {
      if (s !== undefined) scale = s;
      if (!running) { acc = 0; return 0; }
      acc += Math.max(0, realDt) * P.effective();
      const lim = maxTicks * tick;
      if (acc > lim) acc = lim; // spiral of death guard: drop the backlog instead of stalling
      let n = Math.floor(acc / tick + 1e-9);
      if (n > maxTicks) n = maxTicks;
      acc -= n * tick; if (acc < 0) acc = 0;
      return n;
    },
    /** worker ms spent on the last batch (steps + send); returns true when the cap changed */
    report(workMs, ticks, realDt) {
      if (ticks > 0 && workMs >= 0) { const per = workMs / ticks; msPerTick = msPerTick ? msPerTick * 0.8 + per * 0.2 : per; }
      winT += Math.max(0, realDt);
      if (announce) { announce = false; return true; } // the client still knows the old cap
      if (winT < window || !msPerTick) return false;
      winT = 0;
      // largest scale whose ticks fit into `budget` of the worker's time
      const sustain = budget * tick * 1000 / msPerTick;
      let next = cap;
      if (sustain < cap) next = Math.max(SLOWMO_MIN, floor05(sustain));
      else if (sustain > cap * 1.15) next = Math.min(SLOWMO_MAX, Math.max(cap, floor05(sustain)));
      if (next === cap) return false;
      cap = next; return true;
    },
    /** ms until the next tick is due (woken at least every maxWaitMs so new commands apply quickly) */
    delay() {
      const e = P.effective();
      const due = (tick - acc) / e * 1000;
      return clamp(due, e > 1 ? 12 : 1, maxWaitMs);
    },
    get msPerTick() { return msPerTick; },
  };
  return P;
}
